import { Col, Divider, Flex, Progress, Row, Space, Statistic, Typography } from "antd"
import { centsToDollarsDisplay, percentageDisplayWhole } from "../../displayFormatters"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faCircleDollar, faCircleDown, faCircleUp, faHourglass } from "@fortawesome/pro-solid-svg-icons"
import { ReactNode, useContext } from "react"
import { OverviewData } from "./List/workareaGroupsListModel"
import { ItemsContext } from "../../Items/ItemsRoot"
import { ItemClasses } from "../../../common/constants"
import { green, red, yellow } from "@ant-design/colors"

const PROGRESS_TRAIL_COLOR = "#F0F0F0"
const { Text, Title } = Typography

type Props = {
  overviewData: OverviewData
}

const JobPlanOverview = ({ overviewData }: Props) => {
  const quotedPriceDiff = overviewData.total - overviewData.quotedTotal
  const isPositive = quotedPriceDiff > 0

  return <Space direction="vertical" size="small" style={{ width: '100%' }}>
    <Row>
      <Col span={8}>
        <Space size="small" align="end">
          <Statistic
            title="Total"
            value={centsToDollarsDisplay(overviewData.total) ?? 0}
            valueStyle={{ fontWeight: 500 }}
          />
          <Text type="secondary" style={{ fontSize: 16, fontWeight: 500 }}>{Math.round(overviewData.estimatedHours)} hrs</Text>
        </Space>
      </Col>
      <Col span={8}>
        <Flex align="stretch" style={{ height: '100%' }}>
          <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />
          <Statistic
            title="Break Even"
            value={centsToDollarsDisplay(overviewData.breakEven) ?? 0}
            style={{ marginLeft: 16 }}
            valueStyle={{ fontWeight: 500 }}
          />
        </Flex>
      </Col>
      <Col span={8}>
        <NetProfitSection
          netProfit={overviewData.netProfit}
          netProfitPercentage={overviewData.netProfitPercentage}
          timeAndMaterialsTotal={overviewData.timeAndMaterialsTotal}
          quotedTotal={overviewData.quotedTotal}
        />
      </Col>
    </Row>
    <Divider />
    <Row>
      {overviewData.quotedTotal > 0 && <Col span={8}>
        <Space direction="vertical">
          <Pill text="Quoted" icon={<FontAwesomeIcon icon={faCircleDollar} color="#505050" />} />
          <Space align="end">
            <Statistic
              title="Total" value={centsToDollarsDisplay(overviewData.quotedTotal) ?? 0}
              valueStyle={{ fontWeight: 500, fontSize: 16 }}
            />
            <Text type="secondary">
              <FontAwesomeIcon icon={isPositive ? faCircleUp : faCircleDown} />
            </Text>
            <Text type="secondary" style={{ fontSize: 16, fontWeight: 500 }}>{Math.round(overviewData.quotedHours)} hrs</Text>
          </Space>
        </Space>
      </Col>}
      {overviewData.timeAndMaterialsTotal > 0 && <Col span={8}>
        <Flex align="stretch" gap={20} style={{ height: '100%' }}>
          {overviewData.quotedTotal > 0 && <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />}
          <Space direction="vertical">
            <Pill text="Time and Materials" icon={<FontAwesomeIcon icon={faHourglass} color="#505050" />} />
            <Space align="end">
              <Statistic
                title="Total"
                value={centsToDollarsDisplay(overviewData.timeAndMaterialsTotal) ?? 0}
                valueStyle={{ fontWeight: 500, fontSize: 16 }}
              />
              <Text type="secondary" style={{ fontSize: 16, fontWeight: 500 }}>{Math.round(overviewData.timeAndMaterialsHours)} hrs</Text>
            </Space>
          </Space>
        </Flex>
      </Col>}
      {overviewData.perVisitTotal > 0 && <Col span={8}>
        <Flex align="stretch" gap={20} style={{ height: '100%' }}>
          {overviewData.timeAndMaterialsTotal > 0 && <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />}
          <Space direction="vertical">
            <Pill text="Per Visit" icon={<FontAwesomeIcon icon={faCalendar} color="#505050" />} />
            <Space align="end">
              <Statistic
                title="Total"
                value={centsToDollarsDisplay(overviewData.perVisitTotal) ?? 0}
                valueStyle={{ fontWeight: 500, fontSize: 16 }}
              />
              <Text type="secondary" style={{ fontSize: 16, fontWeight: 500 }}>{Math.round(overviewData.perVisitHours)} hrs</Text>
            </Space>
          </Space>
        </Flex>
      </Col>}
    </Row>
  </Space>
}

export default JobPlanOverview

const NetProfitSection = ({ 
  netProfit, 
  netProfitPercentage, 
  timeAndMaterialsTotal, 
  quotedTotal 
}: { netProfit: number, netProfitPercentage: number, timeAndMaterialsTotal: number, quotedTotal: number }) => {
  const { jobData: { budgets } } = useContext(ItemsContext)
  const laborBudget = budgets.find(x => x.class === ItemClasses.Labor)
  const desiredProfitMargingPercentage = laborBudget ? laborBudget.profit : 0
  const raw = netProfitPercentage * 100;
  const isNeg = raw < 0;
  const progressBarPercent = isNeg ? 100 - (Math.abs(raw)) : raw;

  const getNetProfitSectionColor = () => {
    if (raw < desiredProfitMargingPercentage && raw >= 0) {
      return yellow.primary
    }

    if (raw < 0) {
      return red.primary
    }

    return green.primary
  }
  const netProfitSectionColor = getNetProfitSectionColor()

  return <Flex align="stretch" gap={20} style={{ height: '100%' }}>
    {timeAndMaterialsTotal > 0 || quotedTotal > 0 && <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />}
    <Space direction="vertical">
      <Text type="secondary" style={{ fontSize: 14 }}>Net Profit</Text>
      <Space>
        <Flex>
          <Progress
            type="circle"
            percent={progressBarPercent}
            strokeWidth={12}
            size={40}
            strokeColor={isNeg ? PROGRESS_TRAIL_COLOR : netProfitSectionColor}
            trailColor={isNeg ? red.primary : PROGRESS_TRAIL_COLOR}
            strokeLinecap="butt"
            format={() => percentageDisplayWhole(netProfitPercentage)}
            style={{ fontWeight: 500 }}
          />
        </Flex>
        <Title level={3} style={{ color: netProfitSectionColor, marginBottom: 0 }}>{centsToDollarsDisplay(netProfit) ?? 0}</Title>
      </Space>
    </Space>
  </Flex>
}

const Pill = ({ text, icon }: { text: string, icon: ReactNode }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px', backgroundColor: '#fafafa', borderRadius: '32px', width: 'fit-content', gap: '4px' }}>
      {icon}
      <Text type="secondary">{text}</Text>
    </div>
  )
}